import { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    emptyProductDetailColumns,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";
import { scheduleService } from "@/api/services/scheduleService";
import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat, fmtNum } from "@zhihaoscm/utils";
import { areaLib } from "@/utils/areaLib";
import type { ScheduleDateVo } from "@zhihaoscm/entity";

export type indexProps = ProductDetailTabProps;

const CALENDAR_PAGE_SIZE = 15;

function addMonths(value: string, months: number): Date {
    const date = new Date(value);
    date.setMonth(date.getMonth() + months);
    return date;
}

function formatArea(product: Record<string, any>): string {
    const area = areaLib.getFullAreaName(product.provinceCode, product.cityCode, product.regionCode);
    const codes = [product.provinceCode, product.cityCode, product.regionCode].filter(Boolean).join("/");
    const address = String(product.address || "");
    // 旧 Vue 在地址已经包含地区名称时保留编码前缀，避免出现重复的省市区文本。
    if (address && area && (address.includes(area) || area.endsWith(address))) {
        return [codes, address].filter(Boolean).join(" ") || "--";
    }
    return [area, address].filter(Boolean).join(" ") || [codes, address].filter(Boolean).join(" ") || "--";
}

function createFallbackCalendar(start: string, end: string): ScheduleDateVo[] {
    const first = new Date(start);
    const last = addMonths(end, 2);
    const dates: ScheduleDateVo[] = [];
    for (const cursor = new Date(first); cursor <= last; cursor.setDate(cursor.getDate() + 1)) {
        dates.push({
            scheduleDate: cursor.toISOString(),
            scheduleVos: [],
            output: null as never,
            remainAvailablePickedUpTon: 0,
            scheduledTon: 0
        });
    }
    return dates;
}

/** components / caclenderTab / index：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: indexProps) {
    const product = props.data?.productConsignment || {};
    const [calendarData, setCalendarData] = useState<ScheduleDateVo[]>([]);
    const [calendarPage, setCalendarPage] = useState(0);
    const [totalTon, setTotalTon] = useState<number>(0);
    const deliveryPeriod =
        product.deliveryStartTime && product.deliveryEndTime
            ? `${String(product.deliveryStartTime).slice(0, 10)} 至 ${String(product.deliveryEndTime).slice(0, 10)}`
            : "--";
    const location = formatArea(product);

    useEffect(() => {
        let disposed = false;
        const productId = product.id;
        if (!product.deliveryStartTime || !product.deliveryEndTime) {
            setCalendarData([]);
            setTotalTon(0);
            return;
        }
        // 先按提货周期绘制稳定的空排期卡片，接口稍后返回时再替换为真实分组数据。
        setCalendarData(createFallbackCalendar(product.deliveryStartTime, product.deliveryEndTime));
        if (!productId) return;
        const beginTime = dateFormat(product.deliveryStartTime, LOG_DATE_FORMAT_START);
        const endTime = dateFormat(addMonths(product.deliveryEndTime, 2), LOG_DATE_FORMAT_END);
        setCalendarPage(0);
        void Promise.all([
            scheduleService.scheduleDate({ beginTime, endTime, productId: String(productId) }),
            scheduleService.amount({ productId: String(productId) })
        ])
            .then(([dates, amount]) => {
                if (disposed) return;
                setCalendarData((dates || []).map(item => ({ ...item, scheduleVos: item.scheduleVos || [] })));
                setTotalTon(Number(amount || 0));
            })
            .catch(() => {
                if (disposed) return;
                setTotalTon(0);
            });
        return () => {
            disposed = true;
        };
    }, [product.id, product.deliveryStartTime, product.deliveryEndTime]);

    const visibleCalendarData = useMemo(
        () => calendarData.slice(calendarPage * CALENDAR_PAGE_SIZE, (calendarPage + 1) * CALENDAR_PAGE_SIZE),
        [calendarData, calendarPage]
    );
    const calendarPageCount = Math.max(1, Math.ceil(calendarData.length / CALENDAR_PAGE_SIZE));
    return (
        <div className="product-detail-tab-content calender-tab">
            <ProductDetailTabSection title="提货信息">
                <div className="product-detail-delivery-metrics">
                    <div className="product-detail-delivery-metric"><span>提货周期</span><strong>{deliveryPeriod}</strong></div>
                    <div className="product-detail-delivery-metric"><span>提货地点</span><strong>{location}</strong></div>
                </div>
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期日历" className="product-detail-tab-calendar">
                <div className="product-detail-calendar-header">
                    <div className="product-detail-calendar-actions">
                        <Button type="primary" size="middle">修改</Button>
                        <div className="product-detail-calendar-date-range">开始时间　-　结束时间</div>
                        <Button type="primary" size="middle">搜索</Button>
                    </div>
                    <div className="product-detail-calendar-actions">
                        <Button type="primary" ghost size="middle" disabled={calendarPage <= 0} onClick={() => setCalendarPage(page => Math.max(0, page - 1))}>
                            上一页
                        </Button>
                        <Button type="primary" ghost size="middle" disabled={calendarPage >= calendarPageCount - 1} onClick={() => setCalendarPage(page => Math.min(calendarPageCount - 1, page + 1))}>
                            下一页
                        </Button>
                    </div>
                </div>
                <div className="product-detail-calendar-grid">
                    {visibleCalendarData.map(day => {
                        const date = new Date(day.scheduleDate);
                        const hasSchedule = Boolean(day.scheduleVos?.length);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const expired = date.getTime() < today.getTime();
                        return (
                            <div className={`product-detail-calendar-day${expired ? " is-expired" : ""}`} key={day.scheduleDate}>
                                <div className="product-detail-calendar-date">
                                    <strong>{dateFormat(day.scheduleDate)}</strong>
                                    <span>{date.toLocaleDateString("zh-CN", { weekday: "short" })}</span>
                                    <span className="product-detail-calendar-scheduled">已排{fmtNum(day.scheduledTon || 0)}吨</span>
                                </div>
                                <div className={`product-detail-calendar-estimate${hasSchedule ? " is-active" : ""}`}>
                                    <span>{expired ? "不可提货" : "预估可提"}</span>
                                    {!expired && <strong>{fmtNum(day.remainAvailablePickedUpTon || 0)} 吨</strong>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!calendarData.length && <div className="product-detail-tab-empty">暂无排期</div>}
            </ProductDetailTabSection>
            <ProductDetailTabSection title="排期列表">
                <div className="product-detail-calendar-total">总排期吨数（吨）<strong>{fmtNum(totalTon)}</strong></div>
                <ProductDetailTabTable
                    ariaLabel="排期列表"
                    columns={emptyProductDetailColumns(["排期日期", "排期吨数（吨）", "状态", "操作"])}
                    data={props.items}
                />
            </ProductDetailTabSection>
            {props.children}
        </div>
    );
}
